import React, { useEffect, useMemo, useState } from "react";
import client, { apiError } from "@/api/client";
import { useAuth } from "@/context/AuthContext";
import { useBranch } from "@/context/BranchContext";
import { rupiah, numberFmt } from "@/lib/format";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { usePaged, Paginator } from "@/components/Paginator";
import { Plus, Star, Pencil, Search, Trash2, Package } from "lucide-react";

const empty = () => ({
  name: "", sku: "", barcode: "", category_id: "", image_url: "",
  base_unit: "pcs", units: [{ _k: "u0", name: "pcs", factor: 1, sell_price: 0 }],
  is_favorite: false, active: true, price_mode: "manual", margin_pct: 0, markup_amount: 0,
  ready_to_eat: false, daily_limit: 0,
});

export default function Products() {
  const { user } = useAuth();
  const { effectiveBranch } = useBranch();
  const [items, setItems] = useState([]);
  const [cats, setCats] = useState([]);
  const [q, setQ] = useState("");
  const [catFilter, setCatFilter] = useState("all");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty());
  const [editId, setEditId] = useState(null);

  const load = () => client.get("/products", { params: effectiveBranch ? { branch_id: effectiveBranch } : {} }).then((r) => setItems(r.data));
  useEffect(() => { load(); client.get("/categories").then((r) => setCats(r.data)); /* eslint-disable-next-line */ }, [effectiveBranch]);

  const filtered = useMemo(() => items.filter((p) =>
    p.name.toLowerCase().includes(q.toLowerCase()) &&
    (catFilter === "all" || p.category_id === catFilter)
  ), [items, q, catFilter]);
  const paged = usePaged(filtered, 10);
  const catName = (id) => cats.find((c) => c.id === id)?.name || "-";
  const canEdit = ["super_admin", "admin", "branch_head"].includes(user.role);
  const showCost = user.role !== "branch_head";

  const openNew = () => { setForm(empty()); setEditId(null); setOpen(true); };
  const openEdit = (p) => {
    setForm({ ...p, units: p.units.map((u, i) => ({ ...u, _k: "u" + i + Math.random().toString(36).slice(2) })) });
    setEditId(p.id); setOpen(true);
  };

  const setUnit = (i, patch) => setForm((f) => ({ ...f, units: f.units.map((u, idx) => (idx === i ? { ...u, ...patch } : u)) }));
  const addUnit = () => setForm((f) => ({ ...f, units: [...f.units, { _k: Math.random().toString(36).slice(2), name: "", factor: 1, sell_price: 0 }] }));
  const delUnit = (i) => setForm((f) => ({ ...f, units: f.units.filter((_, idx) => idx !== i) }));

  const save = async () => {
    try {
      const payload = {
        ...form,
        units: form.units.map((u) => ({ name: u.name, factor: Number(u.factor), sell_price: Number(u.sell_price) })),
        margin_pct: Number(form.margin_pct) || 0, markup_amount: Number(form.markup_amount) || 0,
      };
      if (!payload.units.some((u) => u.name === payload.base_unit)) {
        toast.error("Satuan dasar harus ada di daftar satuan"); return;
      }
      if (editId) await client.put(`/products/${editId}`, payload);
      else await client.post("/products", payload);
      toast.success("Produk disimpan");
      setOpen(false); load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const toggleFav = async (p) => { await client.patch(`/products/${p.id}/favorite`); load(); };

  return (
    <div className="p-6 space-y-5 animate-in-up">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight">Produk</h1>
          <p className="text-sm text-muted-foreground">Master data produk, satuan & harga jual</p>
        </div>
        {canEdit && <Button onClick={openNew} data-testid="add-product-btn"><Plus className="mr-2 h-4 w-4" /> Tambah Produk</Button>}
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <div className="relative max-w-sm flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cari produk…" className="pl-9" data-testid="product-search" />
        </div>
        <Select value={catFilter} onValueChange={setCatFilter}>
          <SelectTrigger className="w-52" data-testid="product-cat-filter"><SelectValue placeholder="Semua Kategori" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Semua Kategori</SelectItem>
            {cats.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <div className="rounded-lg border bg-white">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead></TableHead>
              <TableHead>Nama</TableHead>
              <TableHead>Kategori</TableHead>
              <TableHead>Satuan Dasar</TableHead>
              <TableHead className="text-right">Harga Jual</TableHead>
              {showCost && <TableHead className="text-right">HPP Rata2</TableHead>}
              <TableHead className="text-right">Stok</TableHead>
              <TableHead></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {paged.slice.map((p) => {
              const base = p.units.find((u) => u.name === p.base_unit) || p.units[0];
              return (
                <TableRow key={p.id} className="hover:bg-slate-50" data-testid={`product-row-${p.id}`}>
                  <TableCell>
                    <button onClick={() => toggleFav(p)} data-testid={`fav-${p.id}`}>
                      <Star className={`h-4 w-4 ${p.is_favorite ? "fill-amber-400 text-amber-400" : "text-slate-300"}`} />
                    </button>
                  </TableCell>
                  <TableCell className="font-medium">{p.name}{p.active === false && <Badge variant="secondary" className="ml-2">nonaktif</Badge>}</TableCell>
                  <TableCell>{catName(p.category_id)}</TableCell>
                  <TableCell>{p.base_unit}</TableCell>
                  <TableCell className="text-right font-mono">{rupiah(base?.sell_price)}</TableCell>
                  {showCost && <TableCell className="text-right font-mono text-muted-foreground">{rupiah(p.avg_cost)}</TableCell>}
                  <TableCell className="text-right font-mono">
                    <span className={p.stock_qty <= 0 ? "text-rose-600 font-semibold" : ""}>{numberFmt(p.stock_qty)}</span>
                  </TableCell>
                  <TableCell>
                    {canEdit && <Button size="icon" variant="ghost" onClick={() => openEdit(p)} data-testid={`edit-product-${p.id}`}><Pencil className="h-4 w-4" /></Button>}
                  </TableCell>
                </TableRow>
              );
            })}
            {!filtered.length && <TableRow><TableCell colSpan={showCost ? 8 : 7} className="py-10 text-center text-muted-foreground">Tidak ada produk</TableCell></TableRow>}
          </TableBody>
        </Table>
        <Paginator {...paged} />
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg">
          <DialogHeader><DialogTitle>{editId ? "Ubah Produk" : "Tambah Produk"}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2"><Label>Nama Produk</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="form-product-name" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2"><Label>Kategori</Label>
                <Select value={form.category_id || ""} onValueChange={(v) => setForm({ ...form, category_id: v })}>
                  <SelectTrigger data-testid="form-product-cat"><SelectValue placeholder="Pilih" /></SelectTrigger>
                  <SelectContent>{cats.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-2"><Label>Barcode / SKU</Label>
                <Input value={form.barcode} onChange={(e) => setForm({ ...form, barcode: e.target.value })} /></div>
            </div>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>Satuan & Harga (faktor = jumlah satuan dasar)</Label>
                <Button size="sm" variant="outline" onClick={addUnit} type="button"><Plus className="mr-1 h-3 w-3" /> Satuan</Button>
              </div>
              {form.units.map((u, i) => (
                <div key={u._k || i} className="flex items-center gap-2">
                  <Input placeholder="nama (slop)" value={u.name} onChange={(e) => setUnit(i, { name: e.target.value })} className="flex-1" data-testid={`unit-name-${i}`} />
                  <Input placeholder="faktor" type="number" value={u.factor} onChange={(e) => setUnit(i, { factor: e.target.value })} className="w-20" data-testid={`unit-factor-${i}`} />
                  <Input placeholder="harga" type="number" value={u.sell_price} onChange={(e) => setUnit(i, { sell_price: e.target.value })} className="w-28 font-mono" data-testid={`unit-price-${i}`} />
                  {form.units.length > 1 && <button onClick={() => delUnit(i)} className="text-muted-foreground hover:text-destructive"><Trash2 className="h-4 w-4" /></button>}
                </div>
              ))}
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2"><Label>Satuan Dasar</Label>
                <Select value={form.base_unit} onValueChange={(v) => setForm({ ...form, base_unit: v })}>
                  <SelectTrigger data-testid="form-base-unit"><SelectValue /></SelectTrigger>
                  <SelectContent>{form.units.filter((u) => u.name).map((u) => <SelectItem key={u.name} value={u.name}>{u.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="flex items-end gap-4">
                <label className="flex items-center gap-2"><Switch checked={form.is_favorite} onCheckedChange={(v) => setForm({ ...form, is_favorite: v })} /> Favorit</label>
                <label className="flex items-center gap-2"><Switch checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} /> Aktif</label>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <label className="flex items-center gap-2"><Switch checked={form.ready_to_eat} onCheckedChange={(v) => setForm({ ...form, ready_to_eat: v })} data-testid="form-ready-to-eat" /> Makanan Siap Saji</label>
              {form.ready_to_eat && (
                <div className="space-y-1"><Label className="text-xs">Batas porsi/hari (0 = bebas)</Label>
                  <Input type="number" value={form.daily_limit} onChange={(e) => setForm({ ...form, daily_limit: Number(e.target.value) })} className="font-mono" data-testid="form-daily-limit" /></div>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Batal</Button>
            <Button onClick={save} data-testid="save-product-btn">Simpan</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
